import { db } from '../db/indexeddb';
import { shouldMakeAPICall } from '../utils/network';
import axios from 'axios';

/**
 * Offline Queue Manager
 * Handles queuing and replaying offline actions
 */
export class OfflineQueue {
  constructor() {
    this.maxRetries = 3;
    this.retryDelay = 1000; // 1 second
  }

  /**
   * Convert FormData to serializable object
   */
  serializeFormData(formData) {
    const obj = {};
    for (const [key, value] of formData.entries()) {
      if (value instanceof File) {
        // For files, convert to base64 for storage
        // Note: This is async, so we'll handle it differently
        obj[key] = {
          type: 'file',
          name: value.name,
          size: value.size,
          type_mime: value.type,
          // File will be converted to base64 when enqueueing
        };
      } else {
        obj[key] = value;
      }
    }
    return obj;
  }

  /**
   * Convert serialized object back to FormData
   */
  deserializeToFormData(obj) {
    const formData = new FormData();
    for (const [key, value] of Object.entries(obj)) {
      if (value && typeof value === 'object' && value.type === 'file') {
        // For files, we need to reconstruct from base64 or file reference
        // This is a limitation - files need to be stored separately
        // For now, we'll skip files in queue (user needs to re-upload when online)
        console.warn(`⚠️ File ${value.name} cannot be queued - will need to be re-uploaded when online`);
        continue;
      } else {
        formData.append(key, value);
      }
    }
    return formData;
  }

  /**
   * Convert File to base64 (for queue storage)
   */
  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Convert base64 back to File
   */
  base64ToFile(base64, filename, mimeType) {
    const arr = base64.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mimeType || mime });
  }

  /**
   * Add action to queue
   * Handles FormData by converting to serializable format
   */
  async enqueue(action, endpoint, payload) {
    try {
      let serializedPayload = payload;

      // If payload is FormData, convert to serializable object
      if (payload instanceof FormData) {
        serializedPayload = {};
        const filePromises = [];

        for (const [key, value] of payload.entries()) {
          if (value instanceof File) {
            // Convert file to base64 for storage
            const base64 = await this.fileToBase64(value);
            serializedPayload[key] = {
              type: 'file',
              name: value.name,
              size: value.size,
              mimeType: value.type,
              base64: base64, // Store as base64
            };
          } else {
            serializedPayload[key] = value;
          }
        }
      }

      const queueItem = {
        action,
        endpoint,
        payload: serializedPayload,
        timestamp: Date.now(),
        retries: 0,
        status: 'pending',
      };

      const id = await db.offlineQueue.add(queueItem);
      console.log(`✅ Queued ${action} action for ${endpoint}`);
      return id;
    } catch (error) {
      console.error('Error enqueueing action:', error);
      throw error;
    }
  }

  /**
   * Get all pending actions
   */
  async getPending() {
    try {
      return await db.offlineQueue
        .where('status')
        .equals('pending')
        .sortBy('timestamp');
    } catch (error) {
      console.error('Error getting pending actions:', error);
      return [];
    }
  }

  /**
   * Mark action as syncing
   */
  async markSyncing(id) {
    try {
      await db.offlineQueue.update(id, {
        status: 'syncing',
      });
    } catch (error) {
      console.error('Error marking action as syncing:', error);
    }
  }

  /**
   * Mark action as completed
   */
  async markCompleted(id) {
    try {
      await db.offlineQueue.update(id, {
        status: 'completed',
      });
      // Optionally delete after some time
      setTimeout(() => {
        db.offlineQueue.delete(id).catch(() => {});
      }, 60000); // Delete after 1 minute
    } catch (error) {
      console.error('Error marking action as completed:', error);
    }
  }

  /**
   * Mark action as failed
   */
  async markFailed(id) {
    try {
      const action = await db.offlineQueue.get(id);
      if (action) {
        const newRetries = action.retries + 1;
        await db.offlineQueue.update(id, {
          status: newRetries >= this.maxRetries ? 'failed' : 'pending',
          retries: newRetries,
        });
      }
    } catch (error) {
      console.error('Error marking action as failed:', error);
    }
  }

  /**
   * Remove action from queue
   */
  async remove(id) {
    try {
      await db.offlineQueue.delete(id);
    } catch (error) {
      console.error('Error removing action from queue:', error);
    }
  }

  /**
   * Clear all completed actions
   */
  async clearCompleted() {
    try {
      await db.offlineQueue
        .where('status')
        .equals('completed')
        .delete();
    } catch (error) {
      console.error('Error clearing completed actions:', error);
    }
  }

  /**
   * Get queue size
   */
  async getSize() {
    try {
      return await db.offlineQueue
        .where('status')
        .anyOf(['pending', 'syncing'])
        .count();
    } catch (error) {
      return 0;
    }
  }

  /**
   * Process single action
   */
  async processAction(action, authToken, userToken) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      console.warn('⚠️ Cannot process action - offline or not authenticated', {
        hasAuthToken: !!authToken,
        hasUserToken: !!userToken,
        isOnline: typeof navigator !== 'undefined' ? navigator.onLine : false,
      });
      return false;
    }

    try {
      console.log(`🔄 Starting to process ${action.action} action (ID: ${action.id})`);
      await this.markSyncing(action.id);

      // ALWAYS reconstruct FormData from serialized payload
      // The API expects FormData, not a plain object
      let payload = action.payload;
      if (payload && typeof payload === 'object' && !(payload instanceof FormData)) {
        // Reconstruct FormData from serialized object
        payload = new FormData();
        for (const [key, value] of Object.entries(action.payload)) {
          if (value && typeof value === 'object' && value.type === 'file') {
            // Convert base64 back to File
            if (value.base64) {
              try {
                const file = this.base64ToFile(
                  value.base64,
                  value.name,
                  value.mimeType
                );
                payload.append(key, file);
              } catch (fileError) {
                console.error(`⚠️ Error reconstructing file ${value.name}:`, fileError);
                // Skip file if reconstruction fails
              }
            } else {
              console.warn(`⚠️ File ${value.name} missing base64 data - skipping`);
            }
          } else {
            // Regular field - append as-is (string, number, etc.)
            // Handle null/undefined values
            if (value !== null && value !== undefined) {
              payload.append(key, value);
            }
          }
        }
        
        // Verify FormData was reconstructed correctly
        const formDataKeys = [];
        for (const key of payload.keys()) {
          formDataKeys.push(key);
        }
        console.log(`✅ Reconstructed FormData for ${action.action} action with ${formDataKeys.length} fields:`, formDataKeys);
        
        // IMPORTANT: For CREATE actions, remove pr_token if present (server will generate it)
        // This fixes cases where pr_token was incorrectly added to create actions
        if (action.action === 'create' && formDataKeys.includes('pr_token')) {
          console.warn('⚠️ Found pr_token in CREATE action - removing it (server will generate new one)');
          // Remove pr_token from FormData
          const newFormData = new FormData();
          for (const [key, value] of payload.entries()) {
            if (key !== 'pr_token') {
              newFormData.append(key, value);
            }
          }
          payload = newFormData;
          console.log('✅ Removed pr_token from CREATE action payload');
        }
      } else if (payload instanceof FormData) {
        // Already FormData, use as-is
        // But still check for pr_token in CREATE actions
        if (action.action === 'create' && payload.has('pr_token')) {
          console.warn('⚠️ Found pr_token in CREATE action FormData - removing it');
          const newFormData = new FormData();
          for (const [key, value] of payload.entries()) {
            if (key !== 'pr_token') {
              newFormData.append(key, value);
            }
          }
          payload = newFormData;
          console.log('✅ Removed pr_token from CREATE action FormData');
        }
        console.log('✅ Payload is already FormData');
      } else {
        console.warn('⚠️ Payload is not FormData and not a plain object:', typeof payload);
      }

      // Execute API call based on action type
      let response;
      switch (action.action) {
        case 'create':
        case 'update':
          console.log(`🔄 Processing ${action.action} action: ${action.endpoint}`);
          response = await axios.post(action.endpoint, payload, {
            headers: {
              Authorization: `Bearer ${authToken}`,
              'Content-Type': 'multipart/form-data',
            },
          });
          break;
        case 'delete':
          response = await axios.delete(action.endpoint, {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          });
          break;
      }

      if (response?.status === 200 || response?.status === 201) {
        console.log(`✅ ${action.action} action completed successfully`);
        await this.markCompleted(action.id);
        return true;
      } else {
        console.warn(`⚠️ ${action.action} action returned status ${response?.status}`);
        await this.markFailed(action.id);
        return false;
      }
    } catch (error) {
      console.error(`❌ Error processing ${action.action} action:`, error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
        endpoint: action.endpoint,
        actionType: action.action,
      });
      
      // Log payload keys for debugging (but not the actual values for security)
      if (action.payload && typeof action.payload === 'object') {
        const payloadKeys = Object.keys(action.payload);
        console.error('Payload keys:', payloadKeys);
        
        // Check if pr_token is in CREATE action (this is wrong!)
        if (action.action === 'create' && payloadKeys.includes('pr_token')) {
          console.error('⚠️ ERROR: pr_token found in CREATE action payload! This will cause API to fail.');
          console.error('⚠️ The pr_token should only be in UPDATE actions, not CREATE.');
        }
      }
      
      await this.markFailed(action.id);
      return false;
    }
  }
}

export const offlineQueue = new OfflineQueue();
